import type { TooltipProps } from "recharts";
import { Stack, Typography } from "@altimateai/lego";
import { formatCost } from "@/helpers/utils";
import type { AutoTuneSavingsStackedPoint } from "../types";
import { formatChartDate, type Granularity } from "./chartFormatters";

interface TooltipExtraProps {
  granularity: Granularity;
  currency: string;
}

const SPEND_COLOR = "#FFCE73";
const POTENTIAL_COLOR = "#DB9717";
const REALIZED_COLOR = "#01CD8C";

// Plain JSX factory (not a component) for the legend dot — keeps this file
// to a single component so react/no-multi-comp stays happy.
const dot = (color: string) => (
  <span
    className="al-inline-block al-w-2.5 al-h-2.5 al-me-2 al-rounded-full al-shrink-0"
    style={{ backgroundColor: color }}
  />
);

// Tooltip mirroring the Snowflake warehouse auto-manage graph: a coral
// "Total Cost" pill, then a Spend row and two savings sections (Possible /
// Auto Tune), each with indented DBU + Infra breakdown lines. Kept in its
// own file so it can be unit-tested without mounting the recharts container.
const AutoTuneSavingsTooltip = ({
  active,
  payload,
  label,
  granularity,
  currency,
}: TooltipProps<number, string> & TooltipExtraProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload as AutoTuneSavingsStackedPoint | undefined;
  if (!point) {
    return null;
  }

  const fmt = (n: number) => formatCost(n ?? 0, currency);
  const total = (point.potential ?? 0) + (point.realized ?? 0) + (point.spend ?? 0);

  return (
    <Stack
      direction="column"
      gap="xs"
      className="al-bg-white al-px-3 al-py-2 al-rounded-md al-border al-shadow-md al-min-w-[210px]"
    >
      <Typography size="sm" weight="medium" className="al-font-semibold">
        {formatChartDate(String(label), granularity)}
      </Typography>
      <Typography
        size="sm"
        weight="medium"
        className="al-w-fit al-rounded al-px-2 al-py-1"
        style={{ backgroundColor: "#ff7f5c", color: "white" }}
      >
        Total Cost : {fmt(total)}
      </Typography>

      <Stack align="center" gap="none">
        {dot(SPEND_COLOR)}
        <Typography size="sm">
          Spend: <strong>{fmt(point.spend)}</strong>
        </Typography>
      </Stack>

      <Stack direction="column" gap="none">
        <Stack align="center" gap="none">
          {dot(POTENTIAL_COLOR)}
          <Typography size="sm">
            Possible Savings: <strong>{fmt(point.potential)}</strong>
          </Typography>
        </Stack>
        <Typography size="sm" className="al-ms-4">
          DBU: <strong>{fmt(point.potentialDbu)}</strong>
        </Typography>
        <Typography size="sm" className="al-ms-4">
          Infra: <strong>{fmt(point.potentialInfra)}</strong>
        </Typography>
      </Stack>

      <Stack direction="column" gap="none">
        <Stack align="center" gap="none">
          {dot(REALIZED_COLOR)}
          <Typography size="sm">
            Auto Tune Savings: <strong>{fmt(point.realized)}</strong>
          </Typography>
        </Stack>
        <Typography size="sm" className="al-ms-4">
          DBU: <strong>{fmt(point.realizedDbu)}</strong>
        </Typography>
        <Typography size="sm" className="al-ms-4">
          Infra: <strong>{fmt(point.realizedInfra)}</strong>
        </Typography>
      </Stack>
    </Stack>
  );
};

export default AutoTuneSavingsTooltip;
